import { ApiError } from "../../data/ApiError";
import {getDefaultLanguage} from "../../data/LocalLanguage";
import i18n from "../../i18n/i18n";
type HttpMethod = "GET" | "PUT" | "POST" | "DELETE";

// HTTP method constants. Should become unnecessary when methods below
// will be rewritten using TypeScript.
export const GET = "GET";
export const PUT = "PUT";
export const POST = "POST";
export const DELETE = "DELETE";

const methods = [GET, PUT, POST, DELETE];

/**
 * Sends a request with given parameters. Default headers include JSON Content-Type
 * and a Bearer Token authorization.
 * @param method (required) A string specifying which HTTP method should be used.
 * Must be equal to "GET", "PUT", "POST", or "DELETE".
 * @param url (required) A URL of the request
 * @param body (optional) A body to be send with a request.
 * @param customHeaders (optional) An object of headers to be appended to usual headers.
 * @returns {Promise<Response>}
 */
async function performRequest(
    method: HttpMethod,
    url: string,
    body?: BodyInit,
    customHeaders?: { [k: string]: string }
): Promise<Response> {
    if (!methods.includes(method)) {
        throw new Error(`invalid method ${method}, not performing the request`);
    }

    if (method === undefined) {
        throw new Error("method mustn't be undefined");
    } else if (method === null) {
        throw new Error("method mustn't be null");
    }

    if (url === undefined) {
        throw new Error("url mustn't be undefined");
    } else if (url === null) {
        throw new Error("url mustn't be null");
    }

    const headers = await getHeaders();
    if (customHeaders) {
        const keys = Object.keys(customHeaders);
        keys.forEach((key) => {
            headers.set(key, customHeaders[key]);
        });
    }

    const options = {
        method,
        headers,
        body: typeof body === "string" ? body : JSON.stringify(body),
    };

    const apiUrl = process.env["REACT_APP_API_URL"] ?? "";
    const fullUrl = `${apiUrl}${url}`;
    return fetch(new Request(fullUrl, options));
}

export async function handleSolutionCheckRequest(
    method: HttpMethod,
    url: string,
    body?: any,
    customHeaders?: { [k: string]: string }
) {
    return performRequest(method, url, body, customHeaders).then((response) => {
        return response.json().then((validationResult) => {
            return { status: response.status, validationResult };
        });
    });
}

// TODO Change after RestService rewritten to TS to be like 'performRequestWithTypedResponse' in Admin
export async function handleRequest(
    method: HttpMethod,
    url: string,
    body?: any,
    customHeaders?: { [k: string]: string }
) {
    return performRequest(method, url, body, customHeaders).then((response) => {
        if (response.ok) {
            return response.json();
        } else {
            return handleError(response);
        }
    });
}

export async function performRequestWithNoResponse(
    method: HttpMethod,
    url: string,
    body?: any,
    customHeaders?: { [k: string]: string }
): Promise<void> {
    return performRequest(method, url, body, customHeaders).then((response) => {
        if (response.ok) {
            return;
        } else {
            return handleError(response);
        }
    });
}

function handleError(response: Response) {
    return response
        .json()
        .then((err: { message: string }) => {
            return { status: response.status, message: err.message };
        })
        .catch((_) => {
            return {
                status: response.status,
                message: "You are probably not logged in!",
            };
        })
        .then((error: ApiError) => {
            throw error;
        });
}

async function getHeaders(): Promise<Headers> {
    let headers = new Headers();
    headers.append("Content-Type", "application/json");
    headers.set("Accept-Language", `${i18n.language}, ${getDefaultLanguage().symbol}`)

    return headers;
}
